"use client"

import * as React from "react"
import * as SheetPrimitive from "@radix-ui/react-dialog"
import {
  IconLoader2,
  IconTrendingUp,
  IconTrendingDown,
  IconMinus,
  IconFileText,
} from "@tabler/icons-react"
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts"
import { format } from "date-fns"
import type { DateRange } from "react-day-picker"
import type { SortingState } from "@tanstack/react-table"

import { useBreakdownTrends } from "@/data/hooks/api/useBreakdown"
import { useActivityLogs } from "@/data/hooks/api/useActivityLogs"
import { TransactionDetailsSidebar } from "@/components/Activity"
import { DataTable } from "@/components/ui/data-table"
import { ProviderIcon } from "@/components/provider-pill"
import { cn, formatCurrency } from "@/lib/utils"
import { logsTableColumns } from "@/pages/CostExplorer/Activity/logic"
import type { TableRowData, BreakdownTimeSeriesDataPoint, MetadataFilter } from "@/data/types/breakdown.types"
import type { ModelSelection, ActivityFilters, ActivityMetadataFilter } from "@/data/types/analytics.types"
import type { LogEntry } from "@/data/types/costExplorer.types"
import type { ActivityLogEntry } from "@/data/types/analytics.types"

// ============================================================================
// Types
// ============================================================================

interface BreakdownDetailSidebarProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rowData: TableRowData | null
  groupBy: string
  dateRange: DateRange | undefined
  granularity: string
  modelSelection: ModelSelection
  metadataFilters: MetadataFilter[]
  timeSeriesData: BreakdownTimeSeriesDataPoint[]
}

// ============================================================================
// Stat Card Component
// ============================================================================

function StatCard({
  label,
  value,
  subtitle,
  trendPct,
  isLoading,
}: {
  label: string
  value: string
  subtitle?: string
  trendPct: number | null | undefined
  isLoading: boolean
}) {
  const trendColor =
    trendPct === null || trendPct === undefined
      ? "text-[#8A8F98]"
      : trendPct > 0
        ? "text-[#E5484D]" // Red for cost increase
        : trendPct < 0
          ? "text-[#30A46C]" // Green for cost decrease
          : "text-[#8A8F98]"

  const TrendIcon =
    trendPct === null || trendPct === undefined
      ? IconMinus
      : trendPct > 0
        ? IconTrendingUp
        : trendPct < 0
          ? IconTrendingDown
          : IconMinus

  return (
    <div className="rounded-[8px] border border-[#EDEEF3] bg-white p-[12px] space-y-[4px]">
      <p className="text-[11px] font-medium text-[#8A8F98] uppercase tracking-wide">
        {label}
      </p>
      <p className="text-[18px] font-semibold text-[#08090A] font-mono leading-tight">
        {value}
      </p>
      <div className="flex items-center justify-between">
        {subtitle && (
          <span className="text-[11px] text-[#8A8F98]">{subtitle}</span>
        )}
        {isLoading ? (
          <IconLoader2 className="size-3 text-[#8A8F98] animate-spin" />
        ) : trendPct !== null && trendPct !== undefined ? (
          <span className={cn("flex items-center gap-[2px] text-[11px] font-medium", trendColor)}>
            <TrendIcon className="size-3" />
            {Math.abs(trendPct).toFixed(1)}%
          </span>
        ) : null}
      </div>
    </div>
  )
}

// ============================================================================
// Chart Components
// ============================================================================

// Custom label for the peak reference line (daily/monthly area chart)
function PeakLabel({ viewBox, value }: { viewBox?: { x: number; y: number }; value: string }) {
  if (!viewBox) return null
  return (
    <text
      x={viewBox.x + 4}
      y={viewBox.y - 6}
      fill="#30A46C"
      fontSize={11}
      fontWeight={600}
      fontFamily="monospace"
      stroke="none"
    >
      {value}
    </text>
  )
}

function formatChartCurrency(value: number): string {
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`
  if (value >= 1) return `$${value.toFixed(2)}`
  if (value > 0 && value < 0.01) return formatCurrency(value, 4, 6)
  return `$${value.toFixed(2)}`
}

// ---- Heatmap (hourly granularity) ----

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const
const HOURS = Array.from({ length: 24 }, (_, i) => i)

// Interpolate between two hex colors
function lerpColor(a: string, b: string, t: number): string {
  const parse = (hex: string) => {
    const n = parseInt(hex.slice(1), 16)
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff]
  }
  const [r1, g1, b1] = parse(a)
  const [r2, g2, b2] = parse(b)
  const r = Math.round(r1 + (r2 - r1) * t)
  const g = Math.round(g1 + (g2 - g1) * t)
  const bl = Math.round(b1 + (b2 - b1) * t)
  return `#${((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1)}`
}

function getHeatmapColor(value: number, maxValue: number): string {
  if (maxValue === 0 || value === 0) return "#F9FAFB" // gray-25 for empty
  const t = Math.min(value / maxValue, 1)
  // 3-stop gradient: gray-25 -> purple-25 -> purple-200 -> purple-500
  if (t < 0.33) return lerpColor("#F2EEFF", "#C6B9FF", t / 0.33)
  if (t < 0.66) return lerpColor("#C6B9FF", "#8B74F2", (t - 0.33) / 0.33)
  return lerpColor("#8B74F2", "#654EDA", (t - 0.66) / 0.34)
}

function HourlyHeatmap({
  data,
}: {
  data: { timestamp: string; cost: number }[]
}) {
  const [hoveredCell, setHoveredCell] = React.useState<{
    day: number
    hour: number
    cost: number
    x: number
    y: number
  } | null>(null)

  // Build a 7x24 grid (dayOfWeek x hour) aggregating cost
  const { grid, maxCost } = React.useMemo(() => {
    // grid[dayIndex][hour] = total cost
    const g: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0))

    for (const point of data) {
      const date = new Date(point.timestamp)
      // getDay() returns 0=Sun..6=Sat → remap to 0=Mon..6=Sun
      const jsDay = date.getDay()
      const dayIdx = jsDay === 0 ? 6 : jsDay - 1
      const hour = date.getHours()
      g[dayIdx][hour] += point.cost
    }

    let max = 0
    for (const row of g) {
      for (const val of row) {
        if (val > max) max = val
      }
    }

    return { grid: g, maxCost: max }
  }, [data])

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-[12px] text-[#8A8F98]">
        No chart data available
      </div>
    )
  }

  return (
    <div className="relative select-none">
      {/* Grid */}
      <div className="flex flex-col gap-[3px]">
        {/* Day rows */}
        {DAY_LABELS.map((dayLabel, dayIdx) => (
          <div key={dayLabel} className="flex items-center gap-[3px]">
            {/* Day label */}
            <div className="w-[33px] shrink-0 text-right text-[10px] text-[#8A8F98] font-medium pr-[4px]">
              {dayLabel}
            </div>
            {/* Hour cells */}
            {HOURS.map((hour) => {
              const cost = grid[dayIdx][hour]
              return (
                <div
                  key={hour}
                  className="flex-1 aspect-[1.6] rounded-[2px] cursor-default transition-opacity"
                  style={{ backgroundColor: getHeatmapColor(cost, maxCost) }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect()
                    const parentRect = e.currentTarget.closest(".relative")?.getBoundingClientRect()
                    if (parentRect) {
                      setHoveredCell({
                        day: dayIdx,
                        hour,
                        cost,
                        x: rect.left - parentRect.left + rect.width / 2,
                        y: rect.top - parentRect.top,
                      })
                    }
                  }}
                  onMouseLeave={() => setHoveredCell(null)}
                />
              )
            })}
          </div>
        ))}

        {/* Hour labels (bottom / X-axis) */}
        <div className="flex gap-[3px] pl-[36px]">
          {HOURS.map((h) => (
            <div
              key={h}
              className="flex-1 text-center text-[9px] text-[#8A8F98] font-medium leading-[14px]"
            >
              {h % 3 === 0 ? `${h.toString().padStart(2, "0")}:00` : ""}
            </div>
          ))}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredCell && (
        <div
          className="absolute z-10 pointer-events-none bg-white border border-[#EDEEF3] rounded-[6px] px-[10px] py-[6px] shadow-lg"
          style={{
            left: hoveredCell.x,
            top: hoveredCell.y - 8,
            transform: "translate(-50%, -100%)",
          }}
        >
          <p className="text-[11px] font-medium text-[#08090A] whitespace-nowrap">
            {DAY_LABELS[hoveredCell.day]} {hoveredCell.hour.toString().padStart(2, "0")}:00 – {hoveredCell.hour.toString().padStart(2, "0")}:59
          </p>
          <p className="text-[11px] text-[#8A8F98] font-mono">
            {formatCurrency(hoveredCell.cost, 2, 6)}
          </p>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center justify-end gap-[6px] mt-[8px] pr-[2px]">
        <span className="text-[9px] text-[#8A8F98]">Less</span>
        <div className="flex gap-[2px]">
          {[0, 0.2, 0.4, 0.6, 0.8, 1].map((t) => (
            <div
              key={t}
              className="w-[10px] h-[10px] rounded-[2px]"
              style={{ backgroundColor: t === 0 ? "#F9FAFB" : getHeatmapColor(t * maxCost, maxCost) }}
            />
          ))}
        </div>
        <span className="text-[9px] text-[#8A8F98]">More</span>
      </div>
    </div>
  )
}

// ---- Area Chart (daily / monthly) ----

function AreaCostChart({
  data,
  granularity,
}: {
  data: { timestamp: string; cost: number }[]
  granularity: string
}) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] text-[12px] text-[#8A8F98]">
        No chart data available
      </div>
    )
  }

  const maxCost = Math.max(...data.map((d) => d.cost))

  const formatXAxis = (timestamp: string) => {
    const date = new Date(timestamp)
    if (granularity === "monthly") return format(date, "MMM yyyy")
    return format(date, "MMM d")
  }

  const formatTooltipValue = (value: number) => formatCurrency(value, 2, 6)

  return (
    <ResponsiveContainer width="100%" height={200}>
      <AreaChart data={data} margin={{ top: 24, right: 8, bottom: 0, left: 8 }}>
        <defs>
          <linearGradient id="breakdownAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#654EDA" stopOpacity={0.15} />
            <stop offset="100%" stopColor="#654EDA" stopOpacity={0.01} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="timestamp"
          tickFormatter={formatXAxis}
          tick={{ fontSize: 10, fill: "#8A8F98" }}
          axisLine={false}
          tickLine={false}
          interval="preserveStartEnd"
          minTickGap={40}
        />
        <YAxis
          domain={[0, maxCost]}
          axisLine={false}
          tickLine={false}
          hide={true}
        />
        <ReferenceLine
          y={maxCost}
          stroke="#30A46C"
          strokeDasharray="4 4"
          strokeWidth={1}
          isFront={false}
          label={<PeakLabel value={formatChartCurrency(maxCost)} />}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: "#fff",
            border: "1px solid #EDEEF3",
            borderRadius: "6px",
            fontSize: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
          formatter={(value: number) => [formatTooltipValue(value), "Cost"] as [string, string]}
          labelFormatter={formatXAxis}
        />
        <Area
          type="monotone"
          dataKey="cost"
          stroke="#654EDA"
          strokeWidth={1.5}
          fill="url(#breakdownAreaGradient)"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}

// ---- Unified chart wrapper ----

function MiniCostChart({
  data,
  granularity,
}: {
  data: { timestamp: string; cost: number }[]
  granularity: string
}) {
  if (granularity === "hourly") {
    return <HourlyHeatmap data={data} />
  }
  return <AreaCostChart data={data} granularity={granularity} />
}

// ============================================================================
// Dimension label helper
// ============================================================================

function getDimensionLabel(groupBy: string): string {
  switch (groupBy) {
    case "provider":
      return "Provider"
    case "model":
      return "Model"
    case "modality":
      return "Modality"
    default:
      return groupBy // metadata key name
  }
}

// ============================================================================
// Map API response to LogEntry format (same as Activity page)
// ============================================================================

function mapApiResponseToLogEntry(entry: ActivityLogEntry): LogEntry {
  const timestamp = entry.timestamp || (entry as any).created_at || ""
  const cost = typeof entry.cost === "string" ? parseFloat(entry.cost) : entry.cost
  const metadata = entry.metadata || (entry as any).request_metadata || {}
  const tools = (entry as any).tools || {}
  const usage = (entry as any).usage || "0 tokens"

  return {
    id: entry.id,
    timestamp,
    provider: entry.provider || "",
    model: entry.model,
    cost: isNaN(cost) ? 0 : cost,
    usage,
    tools: tools as Record<string, string>,
    metadata: metadata as Record<string, string>,
    modality: (entry as any).modality || "",
  }
}

// ============================================================================
// Main Component
// ============================================================================

export function BreakdownDetailSidebar({
  open,
  onOpenChange,
  rowData,
  groupBy,
  dateRange,
  granularity,
  modelSelection,
  metadataFilters,
  timeSeriesData,
}: BreakdownDetailSidebarProps) {
  // Activity table state
  const [pageIndex, setPageIndex] = React.useState(0)
  const [pageSize, setPageSize] = React.useState(10)
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: "timestamp", desc: true },
  ])

  // Nested transaction sidebar state
  const [selectedTransactionId, setSelectedTransactionId] = React.useState<string | null>(null)
  const [isTransactionSidebarOpen, setIsTransactionSidebarOpen] = React.useState(false)

  // Reset table state when row changes
  React.useEffect(() => {
    setPageIndex(0)
    setSorting([{ id: "timestamp", desc: true }])
    setSelectedTransactionId(null)
    setIsTransactionSidebarOpen(false)
  }, [rowData?.group])

  // Build trends request
  const trendsRequest = React.useMemo(() => {
    if (!open || !rowData || !dateRange?.from || !dateRange?.to) return null

    const startDate = new Date(dateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(dateRange.to)
    endDate.setHours(23, 59, 59, 999)

    return {
      date_from: startDate.toISOString(),
      date_to: endDate.toISOString(),
      group: rowData.group,
      group_by: groupBy,
      granularity: granularity as "hourly" | "daily" | "monthly",
      model_selection: modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
        ? modelSelection
        : null,
      metadata: metadataFilters.length > 0 ? metadataFilters : null,
    }
  }, [open, rowData, dateRange, groupBy, granularity, modelSelection, metadataFilters])

  const { data: trends, isLoading: isLoadingTrends } = useBreakdownTrends(trendsRequest, open && !!rowData)

  // Build activity filters for the embedded table
  const activityFilters = React.useMemo((): ActivityFilters | null => {
    if (!open || !rowData || !dateRange?.from || !dateRange?.to) return null

    const startDate = new Date(dateRange.from)
    startDate.setHours(0, 0, 0, 0)
    const endDate = new Date(dateRange.to)
    endDate.setHours(23, 59, 59, 999)

    // Map column ID to API sort_by field
    const sortEntry = sorting[0]
    const sortByMap: Record<string, string> = {
      timestamp: "timestamp",
      provider: "provider",
      model: "model",
      cost: "cost",
    }
    const sortBy = sortEntry ? sortByMap[sortEntry.id] || "timestamp" : "timestamp"
    const sortOrder = sortEntry?.desc ? "desc" : "asc"

    // Convert parent breakdown metadata filters to activity API format
    const activityMetadataFilters: ActivityMetadataFilter[] | undefined =
      metadataFilters.length > 0
        ? metadataFilters.map((f) => ({
          key: f.key,
          operation: f.operator === "eq" ? "is" as const : "is_not" as const,
          value: f.value,
        }))
        : undefined

    return {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
      model_selection: modelSelection.all || modelSelection.providers.length > 0 || modelSelection.models.length > 0
        ? modelSelection
        : null,
      metadata_filters: activityMetadataFilters,
      sort_by: sortBy,
      sort_order: sortOrder as "asc" | "desc",
      skip: pageIndex * pageSize,
      limit: pageSize,
      breakdown_group_by: groupBy,
      breakdown_group_value: rowData.group,
    }
  }, [open, rowData, dateRange, modelSelection, metadataFilters, sorting, pageIndex, pageSize, groupBy])

  const { data: activityResponse, isLoading: isLoadingActivity } = useActivityLogs(activityFilters, open && !!rowData)

  const activityData = React.useMemo(() => {
    if (!activityResponse?.items) return []
    return activityResponse.items.map(mapApiResponseToLogEntry)
  }, [activityResponse])

  const totalActivityItems = activityResponse?.total ?? 0

  // Filter time series data for this group
  const chartData = React.useMemo(() => {
    if (!rowData || !timeSeriesData.length) return []

    const groupName = rowData.group

    // API returns { timestamp, group, cost } per point.
    // Filter to only the selected group and aggregate by timestamp.
    const timestampMap = new Map<string, number>()

    for (const point of timeSeriesData) {
      // Check for { timestamp, group, cost } format (actual API structure)
      if (
        typeof point.group === "string" &&
        point.group === groupName &&
        typeof point.cost === "number"
      ) {
        const ts = point.timestamp as string
        timestampMap.set(ts, (timestampMap.get(ts) || 0) + point.cost)
      }
      // Fallback: dynamic key format { timestamp, [groupName]: cost }
      else if (typeof point[groupName] === "number") {
        const ts = point.timestamp as string
        timestampMap.set(ts, (timestampMap.get(ts) || 0) + (point[groupName] as number))
      }
    }

    return Array.from(timestampMap.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([timestamp, cost]) => ({ timestamp, cost }))
  }, [rowData, timeSeriesData])

  // Handlers
  const handleRowClick = React.useCallback((row: LogEntry) => {
    setSelectedTransactionId(row.id)
    setIsTransactionSidebarOpen(true)
  }, [])

  const handleTransactionSidebarClose = React.useCallback((isOpen: boolean) => {
    setIsTransactionSidebarOpen(isOpen)
    if (!isOpen) {
      setSelectedTransactionId(null)
    }
  }, [])

  return (
    <>
      <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <SheetPrimitive.Portal>
          <SheetPrimitive.Overlay
            className={cn(
              "fixed inset-0 z-50 bg-black/40",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            )}
          />
          <SheetPrimitive.Content
            className={cn(
              "fixed z-50 flex flex-col bg-white shadow-lg",
              "inset-y-0 right-0 h-full w-[1250px] border-l border-[#EDEEF3]",
              "data-[state=open]:animate-in data-[state=closed]:animate-out",
              "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
              "data-[state=closed]:duration-200 data-[state=open]:duration-300"
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-[20px] py-[14px] border-b border-[#EDEEF3] shrink-0">
              <div className="flex items-center gap-[12px]">
                {groupBy === "provider" && rowData && (
                  <ProviderIcon provider={rowData.group} className="w-5 h-5" />
                )}
                <div className="flex flex-col">
                  <SheetPrimitive.Title className="text-[16px] font-semibold text-[#08090A]">
                    {rowData?.group || "—"}
                  </SheetPrimitive.Title>
                  <SheetPrimitive.Description className="text-[12px] text-[#8A8F98] mt-[1px]">
                    Cost breakdown grouped by {getDimensionLabel(groupBy).toLowerCase()}
                  </SheetPrimitive.Description>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {rowData ? (
                <div className="flex flex-col">
                  {/* Stat Cards */}
                  <div className="px-[20px] pt-[16px]">
                    <div className="grid grid-cols-4 gap-[10px]">
                      <StatCard
                        label="Total Cost"
                        value={formatCurrency(rowData.totalCosts, 2, 6)}
                        subtitle={`${rowData.costDistribution.toFixed(1)}% of total`}
                        trendPct={trends?.cost_trend_pct}
                        isLoading={isLoadingTrends}
                      />
                      <StatCard
                        label="Total Events"
                        value={rowData.totalEvents.toLocaleString()}
                        subtitle={`${rowData.eventDistribution.toFixed(1)}% of total`}
                        trendPct={trends?.events_trend_pct}
                        isLoading={isLoadingTrends}
                      />
                      <StatCard
                        label="Cost per Action"
                        value={formatCurrency(rowData.costPerAction, 2, 6)}
                        trendPct={undefined}
                        isLoading={false}
                      />
                      <StatCard
                        label="Daily Average"
                        value={formatCurrency(rowData.dayAverage, 2, 6)}
                        trendPct={undefined}
                        isLoading={false}
                      />
                    </div>
                  </div>

                  {/* Cost Over Time */}
                  <div className="px-[20px] pt-[20px]">
                    <div className="space-y-[10px]">
                      <div className="space-y-[2px]">
                        <h3 className="text-[14px] font-medium text-[#08090A]">
                          Cost Over Time
                        </h3>
                        <p className="text-[12px] text-[#8A8F98]">
                          Spending trend for {rowData.group} over the selected period
                        </p>
                      </div>
                      <div key={granularity} className="rounded-[8px] border border-[#EDEEF3] bg-white p-[12px]">
                        <MiniCostChart data={chartData} granularity={granularity} />
                      </div>
                    </div>
                  </div>

                  {/* Activity Table */}
                  <div className="px-[20px] pt-[20px] pb-[20px]">
                    <div className="space-y-[10px]">
                      <div className="space-y-[2px]">
                        <h3 className="text-[14px] font-medium text-[#08090A]">
                          Request Logs
                        </h3>
                        <p className="text-[12px] text-[#8A8F98]">
                          All API requests where {getDimensionLabel(groupBy).toLowerCase()} is {rowData.group}
                        </p>
                      </div>
                      <DataTable
                        data={activityData}
                        columns={logsTableColumns}
                        loading={isLoadingActivity}
                        getRowId={(row) => row.id}
                        emptyMessage="No logs found"
                        emptyIcon={<IconFileText className="size-6 text-[#8A8F98]" />}
                        enableSorting={true}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        onRowClick={handleRowClick}
                        pagination={{
                          enabled: true,
                          mode: "server",
                          pageIndex,
                          pageSize,
                          totalItems: totalActivityItems,
                          pageSizeOptions: [10, 25, 50],
                          onPageChange: setPageIndex,
                          onPageSizeChange: (newSize) => {
                            setPageSize(newSize)
                            setPageIndex(0)
                          },
                        }}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-[48px] text-center">
                  <p className="text-[12px] text-[#8A8F98]">
                    Select a row to view details
                  </p>
                </div>
              )}
            </div>
          </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
      </SheetPrimitive.Root>

      {/* Nested Transaction Details Sidebar */}
      <TransactionDetailsSidebar
        open={isTransactionSidebarOpen}
        onOpenChange={handleTransactionSidebarClose}
        transactionId={selectedTransactionId}
      />
    </>
  )
}
